// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/* ============================================================
   KinetiQ (openMKM) Landing Page — Full Stylesheet
   Aligned to Sagar's requirements:
   - Standalone product page feel
   - Value proposition + key benefits
   - Web-based differentiator section
   - Video / social media section
   - Contact section with social link slots
   Inspired by: kintekexplorer.com structure
   ============================================================ */

/* ---------- Reset & Base ---------- */
*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

:root {
  --kinetic-blue:     #1A3C6E;
  --kinetic-teal:     #0B8DA6;
  --kinetic-accent:   #00C2A8;
  --kinetic-light:    #faf6f0;
  --kinetic-white:    #FFFFFF;
  --kinetic-dark:     #0D1F35;
  --kinetic-gray:     #6B7A8D;
  --kinetic-border:   #D6E4EC;
  --kinetic-warn:     #F4A261;

  --font-display: 'Inter', 'Segoe UI', sans-serif;
  --font-body:    'Inter', 'Segoe UI', sans-serif;

  --radius-sm: 6px;
  --radius-md: 12px;
  --radius-lg: 20px;

  --shadow-card: 0 4px 24px rgba(10, 50, 90, 0.10);
  --shadow-hero: 0 8px 40px rgba(10, 50, 90, 0.18);
}

html { scroll-behavior: smooth; }

body {
  font-family: var(--font-body);
  color: var(--kinetic-dark);
  background: var(--kinetic-white);
  line-height: 1.65;
  -webkit-font-smoothing: antialiased;
}

a { color: inherit; text-decoration: none; }
img { max-width: 100%; display: block; }

/* ---------- Utility ---------- */
.container {
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 24px;
}

.section-label {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--kinetic-teal);
  margin-bottom: 12px;
}

.section-title {
  font-size: clamp(1.75rem, 3.5vw, 2.5rem);
  font-weight: 700;
  color: var(--kinetic-blue);
  line-height: 1.2;
  margin-bottom: 16px;
}

.section-subtitle {
  font-size: 1.05rem;
  color: var(--kinetic-gray);
  max-width: 620px;
  line-height: 1.7;
}

/* ---------- NAVIGATION ---------- */
.nav {
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--kinetic-white);
  border-bottom: 1px solid var(--kinetic-border);
  box-shadow: 0 2px 12px rgba(26, 60, 110, 0.06);
}

.nav-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 68px;
  gap: 24px;
  /* Edge-to-edge, not the centered 1140px .container used by page
     sections — that centering was leaving a large empty gutter before
     the logo on wider screens. A generous max-width keeps it from
     stretching absurdly on ultra-wide monitors while sitting flush
     near the true edges everywhere realistic. */
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 32px;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.35rem;
  font-weight: 800;
  color: var(--kinetic-blue);
  letter-spacing: -0.02em;
  white-space: nowrap;
  flex-shrink: 0;
}

.nav-logo span {
  color: var(--kinetic-teal);
}

.nav-logo-img {
  max-height: 50px;
  width: auto;
  object-fit: contain;
  object-position: left center;
  display: block;
  border: none;
  outline: none;
  box-shadow: none;
  background: transparent;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 32px;
  list-style: none;
}

.nav-links a {
  font-size: 0.92rem;
  font-weight: 500;
  color: var(--kinetic-gray);
  transition: color 0.2s;
}

.nav-links a:hover { color: var(--kinetic-teal); }

/* Active tab (scroll-spy): the tab for the section currently in view.
   The transparent border on every tab keeps the row from shifting when one is active. */
.nav-links a:not(.nav-help-link) {
  border-bottom: 2px solid transparent;
  padding: 4px 0;
}

.nav-links a.nav-link-active:not(.nav-help-link) {
  color: var(--kinetic-teal);
  font-weight: 700;
  border-bottom-color: var(--kinetic-teal);
}

/* "How to Register" — visually distinct from the scroll-links so it reads
   as help/support, not another page section. Small pill + icon keeps it
   minimal rather than adding a second full-weight CTA next to Login. */
.nav-help-link {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.86rem !important;
  font-weight: 600 !important;
  color: var(--kinetic-teal) !important;
  padding: 6px 12px;
  border: 1px solid var(--kinetic-border);
  border-radius: 999px;
  white-space: nowrap;
  transition: background 0.2s, border-color 0.2s;
}
.nav-help-link:hover {
  background: rgba(11, 141, 166, 0.08);
  border-color: var(--kinetic-teal);
}
.nav-help-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--kinetic-teal);
  color: var(--kinetic-white);
  font-size: 0.7rem;
  font-weight: 800;
  flex-shrink: 0;
}

/* ---------- "How to Register" quick-help modal ---------- */
.rh-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13, 31, 53, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 1000;
  animation: rhFadeIn 0.18s ease;
}

.rh-modal {
  position: relative;
  width: 100%;
  max-width: 560px;
  max-height: 88vh;
  overflow-y: auto;
  background: var(--kinetic-white);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-hero);
  padding: 32px 32px 28px;
  animation: rhSlideUp 0.2s ease;
}

.rh-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border: none;
  background: var(--kinetic-light);
  color: var(--kinetic-gray);
  border-radius: 50%;
  font-size: 1.3rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}
.rh-close:hover { background: var(--kinetic-border); color: var(--kinetic-dark); }

.rh-header { margin-bottom: 20px; padding-right: 28px; }
.rh-tag {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--kinetic-teal);
  margin-bottom: 8px;
}
.rh-header h3 {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--kinetic-blue);
  margin-bottom: 6px;
}
.rh-header p { color: var(--kinetic-gray); font-size: 0.92rem; }

.rh-steps { display: flex; flex-direction: column; gap: 18px; margin-bottom: 22px; }

.rh-step { display: flex; gap: 14px; }

.rh-step-num {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--kinetic-blue);
  color: var(--kinetic-white);
  font-size: 0.82rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rh-step-body { flex: 1 1; min-width: 0; }
.rh-step-title { font-weight: 700; color: var(--kinetic-dark); margin-bottom: 3px; font-size: 0.95rem; }
.rh-step-body p { font-size: 0.88rem; color: var(--kinetic-gray); line-height: 1.55; }
.rh-step-body strong { color: var(--kinetic-dark); }

.rh-shot {
  margin-top: 10px;
  border: 1px solid var(--kinetic-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--kinetic-light);
}
.rh-shot img { width: 100%; display: block; }
.rh-shot-note {
  display: block;
  font-size: 0.74rem;
  color: var(--kinetic-gray);
  padding: 6px 10px;
  border-top: 1px solid var(--kinetic-border);
  background: var(--kinetic-white);
}

.rh-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 16px;
  border-top: 1px solid var(--kinetic-border);
}
.rh-link { font-size: 0.85rem; font-weight: 600; color: var(--kinetic-teal); }
.rh-link:hover { text-decoration: underline; }
.rh-cta {
  background: var(--kinetic-teal);
  color: var(--kinetic-white) !important;
  padding: 10px 20px;
  border-radius: var(--radius-sm);
  font-weight: 600;
  font-size: 0.88rem;
  transition: background 0.2s, transform 0.15s;
}
.rh-cta:hover { background: var(--kinetic-blue); transform: translateY(-1px); }

@keyframes rhFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes rhSlideUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }

@media (max-width: 640px) {
  .nav-help-text { display: none; }
  .nav-help-link { padding: 6px 8px; }
  .rh-modal { padding: 24px 20px 20px; }
}

.nav-cta {
  background: var(--kinetic-teal);
  color: var(--kinetic-white) !important;
  padding: 9px 22px;
  border-radius: var(--radius-sm);
  font-weight: 600 !important;
  font-size: 0.88rem !important;
  transition: background 0.2s, transform 0.15s !important;
}

.nav-cta:hover {
  background: var(--kinetic-blue) !important;
  transform: translateY(-1px);
}

/* ---------- HERO ---------- */
.hero {
  background: linear-gradient(160deg, var(--kinetic-blue) 0%, #0E2A52 55%, var(--kinetic-dark) 100%);
  color: var(--kinetic-white);
  padding: 96px 0 80px;
  overflow: hidden;
  position: relative;
}

.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 70% 60% at 75% 40%, rgba(0,194,168,0.12) 0%, transparent 70%);
  pointer-events: none;
}

.hero-inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: stretch;        /* both columns same height */
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 194, 168, 0.15);
  border: 1px solid rgba(0, 194, 168, 0.35);
  color: var(--kinetic-accent);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 6px 14px;
  border-radius: 99px;
  margin-bottom: 24px;
}

.hero-badge::before {
  content: '';
  width: 7px;
  height: 7px;
  background: var(--kinetic-accent);
  border-radius: 50%;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(1.4); }
}

.hero h1 {
  font-size: clamp(2rem, 4.5vw, 3.2rem);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.025em;
  margin-bottom: 20px;
}

.hero h1 em {
  font-style: normal;
  color: var(--kinetic-accent);
}

.hero-description {
  font-size: 1.1rem;
  color: rgba(255,255,255,0.78);
  line-height: 1.7;
  margin-bottom: 36px;
  max-width: 480px;
}

.hero-actions {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  align-items: center;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--kinetic-accent);
  color: var(--kinetic-dark);
  font-weight: 700;
  font-size: 0.95rem;
  padding: 14px 28px;
  border-radius: var(--radius-sm);
  border: none;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 4px 16px rgba(0,194,168,0.35);
  text-decoration: none;
}

.btn-primary:hover {
  background: #00a896;
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(0,194,168,0.45);
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  color: var(--kinetic-white);
  font-weight: 600;
  font-size: 0.95rem;
  padding: 13px 26px;
  border-radius: var(--radius-sm);
  border: 1.5px solid rgba(255,255,255,0.35);
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  text-decoration: none;
}

.btn-secondary:hover {
  border-color: var(--kinetic-accent);
  background: rgba(0,194,168,0.1);
  color: var(--kinetic-accent);
}

/* Web-based differentiator pill in hero */
.hero-web-pill {
  margin-top: 28px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 99px;
  padding: 8px 16px 8px 10px;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.70);
}

.hero-web-pill .pill-icon {
  width: 26px;
  height: 26px;
  background: var(--kinetic-accent);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.hero-visual {
  position: relative;
  display: flex;
  flex-direction: column;
}

/* Outer card — exactly as tall as the text column, no more */
.hero-carousel-shell {
  flex: 1 1;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-hero);
  border: 1px solid rgba(255,255,255,0.1);
  background: linear-gradient(150deg, rgba(11,141,166,0.20) 0%, rgba(0,194,168,0.07) 100%);
  overflow: hidden;
  padding: 22px 22px 16px;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Carousel wrapper — stretches to fill shell with zero leftover */
.hero-carousel {
  flex: 1 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  color: rgba(255,255,255,0.92);
}

/* Every slide uses the full available height */
.hero-carousel > div:first-child {
  flex: 1 1;
  display: flex;
  flex-direction: column;
}

/* ---------- WEB-BASED DIFFERENTIATOR BANNER ---------- */
.differentiator-banner {
  background: linear-gradient(90deg, var(--kinetic-teal), var(--kinetic-accent));
  color: var(--kinetic-white);
  padding: 18px 0;
  text-align: center;
}

.differentiator-banner-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.differentiator-banner strong {
  font-size: 0.95rem;
  font-weight: 700;
}

.differentiator-banner span {
  font-size: 0.92rem;
  opacity: 0.9;
}

.diff-divider {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(255,255,255,0.5);
}

/* ---------- VALUE PROPOSITION / WHY KINETIC ---------- */
.why-section {
  padding: 88px 0;
  background: var(--kinetic-white);
}

.why-header {
  text-align: center;
  margin-bottom: 60px;
}

.why-header .section-subtitle {
  margin: 0 auto;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 28px;
}

.benefit-card {
  background: var(--kinetic-light);
  border: 1px solid var(--kinetic-border);
  border-radius: var(--radius-md);
  padding: 32px 28px;
  transition: box-shadow 0.25s, transform 0.2s, border-color 0.2s;
  position: relative;
  overflow: hidden;
}

.benefit-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--kinetic-teal), var(--kinetic-accent));
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s;
}

.benefit-card:hover {
  box-shadow: var(--shadow-card);
  transform: translateY(-4px);
  border-color: var(--kinetic-teal);
}

.benefit-card:hover::after { transform: scaleX(1); }

.benefit-icon {
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, var(--kinetic-blue), var(--kinetic-teal));
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 1.4rem;
}

.benefit-card h3 {
  font-size: 1.08rem;
  font-weight: 700;
  color: var(--kinetic-blue);
  margin-bottom: 10px;
}

.benefit-card p {
  font-size: 0.92rem;
  color: var(--kinetic-gray);
  line-height: 1.65;
}

/* ---------- WEB-BASED DIFFERENTIATOR SECTION ---------- */
.web-advantage {
  background: var(--kinetic-blue);
  color: var(--kinetic-white);
  padding: 88px 0;
}

.web-advantage-inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 72px;
  align-items: center;
}

.web-advantage-content h2 {
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 20px;
}

.web-advantage-content h2 em {
  font-style: normal;
  color: var(--kinetic-accent);
}

.web-advantage-content p {
  font-size: 1rem;
  color: rgba(255,255,255,0.75);
  line-height: 1.75;
  margin-bottom: 28px;
}

.comparison-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.comparison-list li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.95rem;
  color: rgba(255,255,255,0.85);
}

.comparison-list li .check {
  width: 22px;
  height: 22px;
  background: var(--kinetic-accent);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.7rem;
  color: var(--kinetic-dark);
  margin-top: 2px;
}

.comparison-list li .cross {
  width: 22px;
  height: 22px;
  background: rgba(255,255,255,0.12);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.7rem;
  color: rgba(255,255,255,0.4);
  margin-top: 2px;
}

.comparison-table {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.comparison-table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  padding: 14px 20px;
  background: rgba(0,194,168,0.15);
  border-bottom: 1px solid rgba(255,255,255,0.1);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--kinetic-accent);
}

.comparison-table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  padding: 13px 20px;
  font-size: 0.9rem;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  align-items: center;
}

.comparison-table-row:last-child { border-bottom: none; }
.comparison-table-row:nth-child(odd) { background: rgba(255,255,255,0.03); }

.comparison-table-row span { color: rgba(255,255,255,0.75); }
.ct-yes { color: var(--kinetic-accent) !important; font-weight: 600; }
.ct-no  { color: rgba(255,255,255,0.25) !important; }

/* ---------- FEATURES SECTION ---------- */
.features-section {
  padding: 88px 0;
  background: var(--kinetic-light);
}

.features-header {
  text-align: center;
  margin-bottom: 60px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.feature-card {
  background: var(--kinetic-white);
  border-radius: var(--radius-md);
  border: 1px solid var(--kinetic-border);
  padding: 30px 26px;
  box-shadow: 0 2px 10px rgba(26,60,110,0.05);
  transition: box-shadow 0.25s;
}

.feature-card:hover { box-shadow: var(--shadow-card); }

.feature-tag {
  display: inline-block;
  background: rgba(11,141,166,0.1);
  color: var(--kinetic-teal);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 99px;
  margin-bottom: 14px;
}

.feature-card h3 {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--kinetic-blue);
  margin-bottom: 10px;
}

.feature-card p {
  font-size: 0.9rem;
  color: var(--kinetic-gray);
  line-height: 1.65;
}

/* ---------- DEMO / VIDEO SECTION ---------- */
.demo-section {
  padding: 88px 0;
  background: var(--kinetic-white);
}

.demo-section-header {
  text-align: center;
  margin-bottom: 52px;
}

.demo-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: stretch;
}

.demo-video-card {
  background: var(--kinetic-dark);
  border-radius: var(--radius-md);
  overflow: hidden;
  box-shadow: var(--shadow-hero);
  display: flex;
  flex-direction: column;
}

.demo-video-thumb {
  aspect-ratio: 16/9;
  background: linear-gradient(135deg, var(--kinetic-blue), #0a2240);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  overflow: hidden;
}

.demo-video-thumb::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 60% 50% at 50% 50%, rgba(0,194,168,0.15), transparent);
}

.play-btn {
  width: 72px;
  height: 72px;
  background: var(--kinetic-accent);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  color: var(--kinetic-dark);
  box-shadow: 0 0 0 0 rgba(0,194,168,0.4);
  animation: ripple 2s ease-out infinite;
  cursor: pointer;
  border: none;
  transition: transform 0.2s;
  position: relative;
  z-index: 1;
}

.play-btn:hover { transform: scale(1.08); }
.play-btn:focus-visible { outline: 2px solid var(--kinetic-white); outline-offset: 3px; }

@keyframes ripple {
  0%   { box-shadow: 0 0 0 0   rgba(0,194,168,0.4); }
  70%  { box-shadow: 0 0 0 22px rgba(0,194,168,0);   }
  100% { box-shadow: 0 0 0 0   rgba(0,194,168,0);   }
}

.demo-video-info {
  padding: 20px 22px;
  color: var(--kinetic-white);
}

.demo-video-info h3 {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 6px;
}

.demo-video-info p {
  font-size: 0.85rem;
  color: rgba(255,255,255,0.55);
}

.demo-video-info a {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--kinetic-accent);
  font-size: 0.85rem;
  font-weight: 600;
  margin-top: 12px;
  transition: gap 0.2s;
}

.demo-video-info a:hover { gap: 10px; }

.demo-cta-card {
  background: var(--kinetic-light);
  border: 1px solid var(--kinetic-border);
  border-radius: var(--radius-md);
  padding: 40px 36px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.demo-cta-card h3 {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--kinetic-blue);
  margin-bottom: 14px;
  line-height: 1.3;
}

.demo-cta-card p {
  font-size: 0.95rem;
  color: var(--kinetic-gray);
  margin-bottom: 28px;
  line-height: 1.65;
}

.social-links-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.social-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: var(--radius-sm);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  text-decoration: none;
}

.social-link-btn:hover { transform: translateY(-2px); box-shadow: var(--shadow-card); }

.social-link-btn.youtube {
  background: #FF0000;
  color: white;
}

.social-link-btn.linkedin {
  background: #0A66C2;
  color: white;
}

/* ---------- TRUSTED BY (logos) ---------- */
.trusted-section {
  padding: 64px 0;
  background: var(--kinetic-light);
  border-top: 1px solid var(--kinetic-border);
  border-bottom: 1px solid var(--kinetic-border);
}

.trusted-header {
  text-align: center;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--kinetic-gray);
  margin-bottom: 36px;
}

.logos-row {
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  justify-content: center;
  align-items: center;
}

.logo-placeholder {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: var(--radius-sm);
  border: 1px solid transparent;
  text-decoration: none;
  color: var(--kinetic-gray);
  opacity: 0.7;
  transition: opacity 0.2s, border-color 0.2s, background 0.2s, color 0.2s;
  cursor: pointer;
}

.logo-placeholder:hover {
  opacity: 1;
  border-color: var(--kinetic-border);
  background: rgba(255,255,255,0.6);
  color: var(--kinetic-navy);
}

.logo-placeholder-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.logo-placeholder-name {
  font-size: 0.88rem;
  font-weight: 600;
  white-space: nowrap;
}

/* ---------- HOW IT WORKS ---------- */
.how-section {
  padding: 88px 0;
  background: var(--kinetic-white);
}

.how-section-header {
  text-align: center;
  margin-bottom: 60px;
}

.steps-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  /* position: relative; */
}

.steps-row::before {
  content: '';
  position: absolute;
  top: 28px;
  left: calc(16.67% + 16px);
  right: calc(16.67% + 16px);
  height: 2px;
  background: linear-gradient(90deg, var(--kinetic-teal), var(--kinetic-accent));
  z-index: 0;
}

.step-card {
  text-align: center;
  padding: 32px 20px;
  background: var(--kinetic-light);
  border-radius: var(--radius-md);
  border: 1px solid var(--kinetic-border);
  position: relative;
  z-index: 1;
}

.step-number {
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, var(--kinetic-teal), var(--kinetic-accent));
  color: white;
  font-weight: 800;
  font-size: 1.1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
}

.step-card h3 {
  font-size: 1rem;
  font-weight: 700;
  color: var(--kinetic-blue);
  margin-bottom: 10px;
}

.step-card p {
  font-size: 0.88rem;
  color: var(--kinetic-gray);
  line-height: 1.65;
}

/* ── FOOTER ─────────────────────────────────────────────────────────────────── */
.footer {
  background: var(--kinetic-white);
  padding: 56px 0 0;
}

/* 4-column grid: brand | quick-links | contact | social */
.footer-inner {
  display: grid;
  grid-template-columns: 1fr 1fr 1.4fr auto;
  gap: 40px 48px;
  align-items: flex-start;
  padding-bottom: 48px;
}

/* ── Brand column ── */
.footer-brand {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.footer-brand-logo {
  height: auto;
  object-fit: contain;
  display: block;
  border: none;
  outline: none;
  box-shadow: none;
  background: transparent;
}

.footer-tagline {
  font-size: 0.82rem;
  color: var(--kinetic-blue);
  line-height: 1.65;
  margin: 0;
}

/* ── Column headings ── */
.footer-col-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #13F01C;             
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 18px;
}

/* ── Quick-links column ── */
.footer-nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 11px;
}

.footer-nav-list a {
  font-size: 0.9rem;
  color: var(--kinetic-blue);
  text-decoration: none;
  transition: color 0.2s;
  line-height: 0.65;
}

.footer-nav-list a:hover { color: #4CAF50; }

/* ── Contact column ── */
.footer-contact-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.footer-contact-item {
  font-size: 0.88rem;
  line-height: 1.55;
  color: var(--kinetic-blue);
}

.footer-contact-item .fci-label {
  color: #13F01C;
  font-weight: 600;
  margin-right: 4px;
}

.footer-contact-item a {
  color: var(--kinetic-blue);
  text-decoration: none;
  transition: color 0.2s;
}

.footer-contact-item a:hover { color: #4CAF50; }

/* ── Social column ── */
.footer-social-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.footer-social-icons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.footer-social-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255,255,255,0.10);
  color: var(--kinetic-blue);
  text-decoration: none;
  transition: background 0.2s, transform 0.2s;
}

.footer-social-icon:hover {
  background: #4CAF50;
  transform: translateY(-2px);
}

/* ── Responsive ── */
@media (max-width: 960px) {
  .footer-inner {
    grid-template-columns: 1fr 1fr;
    gap: 36px 32px;
  }
}

@media (max-width: 560px) {
  .footer-inner {
    grid-template-columns: 1fr;
    gap: 28px;
  }
  .footer-social-icons { gap: 8px; }
}

/* ---------- RESPONSIVE ---------- */
@media (max-width: 900px) {
  .hero-inner,
  .web-advantage-inner,
  .demo-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .hero-visual { display: none; }

  .steps-row {
    grid-template-columns: 1fr;
  }

  .steps-row::before { display: none; }

  .nav-links { display: none; }

  .comparison-table { font-size: 0.82rem; }
}

@media (max-width: 600px) {
  .hero { padding: 64px 0 56px; }
  .hero h1 { font-size: 1.9rem; }
  .benefits-grid { grid-template-columns: 1fr; }
  .features-grid { grid-template-columns: 1fr; }
  .differentiator-banner-inner { flex-direction: column; gap: 6px; }
  .diff-divider { display: none; }
}

/* ── Pipe flow bubble animation (reactor & pipeline slides) ── */
@keyframes pipe-flow {
  0%   { transform: translateX(-12px); opacity: 0; }
  15%  { opacity: 1; }
  85%  { opacity: 1; }
  100% { transform: translateX(12px);  opacity: 0; }
}

@keyframes pipe-flow-v {
  0%   { transform: translateY(-12px); opacity: 0; }
  15%  { opacity: 1; }
  85%  { opacity: 1; }
  100% { transform: translateY(12px);  opacity: 0; }
}

/* ---------- HERO KINETICS CHART — continuous animation ---------- */

/* Curve draw — triggers via key change each cycle */
.kinetic-curve {
  stroke-dasharray: 420;
  stroke-dashoffset: 420;
}
.kinetic-curve.drawn {
  animation: curve-draw 1.1s ease forwards;
}
.kinetic-curve-product.drawn {
  animation: curve-draw 1.1s ease 0.15s forwards;
}

@keyframes curve-draw {
  to { stroke-dashoffset: 0; }
}

/* Dot pop-in (inline animation on each circle element) */
@keyframes dot-pop {
  0%   { opacity: 0; transform: scale(0.4); }
  70%  { opacity: 1; transform: scale(1.15); }
  100% { opacity: 1; transform: scale(1); }
}

/* "Optimising…" blink */
@keyframes hero-blink {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.3; }
}

/* Floating background particles */
.hero-particle {
  position: absolute;
  border-radius: 50%;
  background: rgba(0, 194, 168, 0.18);
  pointer-events: none;
  animation: particle-float var(--dur, 8s) ease-in-out var(--delay, 0s) infinite alternate;
}

@keyframes particle-float {
  0%   { transform: translateY(0px)   scale(1);    opacity: 0.15; }
  50%  { transform: translateY(-18px) scale(1.15); opacity: 0.35; }
  100% { transform: translateY(6px)   scale(0.9);  opacity: 0.10; }
}

@media (prefers-reduced-motion: reduce) {
  .kinetic-curve        { stroke-dashoffset: 0; animation: none; }
  .hero-particle        { animation: none; }
}

/* ---------- LANDING PAGE SCROLL CONTAINER ---------- */
/* See SCROLL FIX note in the JSX: the dashboard sets html,body{overflow:hidden},
   so the landing page owns its own scrolling region instead. */
.kinetic-landing {
  height: 100vh;
  overflow-y: auto;
}

/* ---------- SCROLL ANIMATIONS ---------- */
@media (prefers-reduced-motion: no-preference) {
  .benefit-card,
  .feature-card,
  .step-card {
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.5s ease, transform 0.5s ease, box-shadow 0.25s, border-color 0.2s;
  }

  .benefit-card.visible,
  .feature-card.visible,
  .step-card.visible {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ---------- CAPABILITIES (8-card grid) + INPUT OPTIONS PANEL ---------- */
/* 8 capability cards: four across on desktop so the grid stays 4 x 2. */
.benefits-grid.benefits-grid-8 {
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.benefits-grid-8 .benefit-card { padding: 26px 22px; }

.benefits-grid-8 .benefit-card h3 { font-size: 1.02rem; }

.inputs-panel {
  background: var(--kinetic-white);
  border: 1px solid var(--kinetic-border);
  border-radius: var(--radius-md);
  padding: 34px 32px 30px;
  margin-bottom: 28px;
  box-shadow: 0 2px 10px rgba(26, 60, 110, 0.05);
}

.inputs-panel-head { max-width: 680px; margin-bottom: 26px; }

.inputs-panel-head h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--kinetic-blue);
  margin-bottom: 8px;
}

.inputs-panel-head p {
  font-size: 0.95rem;
  color: var(--kinetic-gray);
  line-height: 1.65;
}

.inputs-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border-top: 1px solid var(--kinetic-border);
}

.inputs-group {
  padding: 22px 22px 6px 0;
  margin-right: 22px;
  border-right: 1px solid var(--kinetic-border);
}

.inputs-group:last-child { border-right: none; margin-right: 0; padding-right: 0; }

.inputs-group h4 {
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--kinetic-blue);
  margin-bottom: 6px;
}

.inputs-group p {
  font-size: 0.86rem;
  color: var(--kinetic-gray);
  line-height: 1.55;
  margin-bottom: 12px;
}

.inputs-chips {
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.inputs-chips li {
  background: rgba(11, 141, 166, 0.1);
  color: var(--kinetic-teal);
  font-size: 0.78rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 99px;
}

@media (max-width: 1000px) {
  .benefits-grid.benefits-grid-8 { grid-template-columns: repeat(2, 1fr); }
  .inputs-grid { grid-template-columns: repeat(2, 1fr); }
  .inputs-group:nth-child(2) { border-right: none; margin-right: 0; padding-right: 0; }
  .inputs-group:nth-child(n+3) { border-top: 1px solid var(--kinetic-border); }
  .inputs-group:nth-child(3) { padding-right: 22px; }
}

@media (max-width: 600px) {
  .benefits-grid.benefits-grid-8 { grid-template-columns: 1fr; }
  .inputs-panel { padding: 26px 20px 20px; }
  .inputs-grid { grid-template-columns: 1fr; }
  .inputs-group,
  .inputs-group:nth-child(3) {
    border-right: none; margin-right: 0; padding-right: 0;
  }
  .inputs-group:nth-child(n+2) { border-top: 1px solid var(--kinetic-border); }
}`, "",{"version":3,"sources":["webpack://./src/styles/KineticLandingPage.css"],"names":[],"mappings":"AAAA;;;;;;;;;iEASiE;;AAEjE,uCAAuC;AACvC;EACE,sBAAsB;EACtB,SAAS;EACT,UAAU;AACZ;;AAEA;EACE,2BAA2B;EAC3B,2BAA2B;EAC3B,2BAA2B;EAC3B,2BAA2B;EAC3B,2BAA2B;EAC3B,2BAA2B;EAC3B,2BAA2B;EAC3B,2BAA2B;EAC3B,2BAA2B;;EAE3B,+CAA+C;EAC/C,+CAA+C;;EAE/C,gBAAgB;EAChB,iBAAiB;EACjB,iBAAiB;;EAEjB,gDAAgD;EAChD,gDAAgD;AAClD;;AAEA,OAAO,uBAAuB,EAAE;;AAEhC;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,gCAAgC;EAChC,iBAAiB;EACjB,mCAAmC;AACrC;;AAEA,IAAI,cAAc,EAAE,qBAAqB,EAAE;AAC3C,MAAM,eAAe,EAAE,cAAc,EAAE;;AAEvC,kCAAkC;AAClC;EACE,iBAAiB;EACjB,cAAc;EACd,eAAe;AACjB;;AAEA;EACE,qBAAqB;EACrB,kBAAkB;EAClB,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;EACzB,0BAA0B;EAC1B,mBAAmB;AACrB;;AAEA;EACE,wCAAwC;EACxC,gBAAgB;EAChB,0BAA0B;EAC1B,gBAAgB;EAChB,mBAAmB;AACrB;;AAEA;EACE,kBAAkB;EAClB,0BAA0B;EAC1B,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA,qCAAqC;AACrC;EACE,gBAAgB;EAChB,MAAM;EACN,YAAY;EACZ,gCAAgC;EAChC,8CAA8C;EAC9C,8CAA8C;AAChD;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,YAAY;EACZ,SAAS;EACT;;;;gDAI8C;EAC9C,iBAAiB;EACjB,cAAc;EACd,eAAe;AACjB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,kBAAkB;EAClB,gBAAgB;EAChB,0BAA0B;EAC1B,uBAAuB;EACvB,mBAAmB;EACnB,cAAc;AAChB;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;EACE,gBAAgB;EAChB,WAAW;EACX,mBAAmB;EACnB,4BAA4B;EAC5B,cAAc;EACd,YAAY;EACZ,aAAa;EACb,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,gBAAgB;AAClB;;AAEA;EACE,kBAAkB;EAClB,gBAAgB;EAChB,0BAA0B;EAC1B,sBAAsB;AACxB;;AAEA,qBAAqB,0BAA0B,EAAE;;AAEjD;wFACwF;AACxF;EACE,oCAAoC;EACpC,cAAc;AAChB;;AAEA;EACE,0BAA0B;EAC1B,gBAAgB;EAChB,wCAAwC;AAC1C;;AAEA;;uEAEuE;AACvE;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,6BAA6B;EAC7B,2BAA2B;EAC3B,qCAAqC;EACrC,iBAAiB;EACjB,uCAAuC;EACvC,oBAAoB;EACpB,mBAAmB;EACnB,8CAA8C;AAChD;AACA;EACE,oCAAoC;EACpC,iCAAiC;AACnC;AACA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,+BAA+B;EAC/B,2BAA2B;EAC3B,iBAAiB;EACjB,gBAAgB;EAChB,cAAc;AAChB;;AAEA,6DAA6D;AAC7D;EACE,eAAe;EACf,QAAQ;EACR,kCAAkC;EAClC,0BAA0B;EAC1B,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,aAAa;EACb,aAAa;EACb,8BAA8B;AAChC;;AAEA;EACE,kBAAkB;EAClB,WAAW;EACX,gBAAgB;EAChB,gBAAgB;EAChB,gBAAgB;EAChB,gCAAgC;EAChC,+BAA+B;EAC/B,8BAA8B;EAC9B,uBAAuB;EACvB,8BAA8B;AAChC;;AAEA;EACE,kBAAkB;EAClB,SAAS;EACT,WAAW;EACX,WAAW;EACX,YAAY;EACZ,YAAY;EACZ,gCAAgC;EAChC,0BAA0B;EAC1B,kBAAkB;EAClB,iBAAiB;EACjB,cAAc;EACd,eAAe;EACf,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,yCAAyC;AAC3C;AACA,kBAAkB,iCAAiC,EAAE,0BAA0B,EAAE;;AAEjF,aAAa,mBAAmB,EAAE,mBAAmB,EAAE;AACvD;EACE,qBAAqB;EACrB,iBAAiB;EACjB,gBAAgB;EAChB,qBAAqB;EACrB,yBAAyB;EACzB,0BAA0B;EAC1B,kBAAkB;AACpB;AACA;EACE,iBAAiB;EACjB,gBAAgB;EAChB,0BAA0B;EAC1B,kBAAkB;AACpB;AACA,eAAe,0BAA0B,EAAE,kBAAkB,EAAE;;AAE/D,YAAY,aAAa,EAAE,sBAAsB,EAAE,SAAS,EAAE,mBAAmB,EAAE;;AAEnF,WAAW,aAAa,EAAE,SAAS,EAAE;;AAErC;EACE,cAAc;EACd,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,+BAA+B;EAC/B,2BAA2B;EAC3B,kBAAkB;EAClB,gBAAgB;EAChB,aAAa;EACb,mBAAmB;EACnB,uBAAuB;AACzB;;AAEA,gBAAgB,SAAO,EAAE,YAAY,EAAE;AACvC,iBAAiB,gBAAgB,EAAE,0BAA0B,EAAE,kBAAkB,EAAE,kBAAkB,EAAE;AACvG,kBAAkB,kBAAkB,EAAE,0BAA0B,EAAE,iBAAiB,EAAE;AACrF,uBAAuB,0BAA0B,EAAE;;AAEnD;EACE,gBAAgB;EAChB,uCAAuC;EACvC,+BAA+B;EAC/B,gBAAgB;EAChB,gCAAgC;AAClC;AACA,eAAe,WAAW,EAAE,cAAc,EAAE;AAC5C;EACE,cAAc;EACd,kBAAkB;EAClB,0BAA0B;EAC1B,iBAAiB;EACjB,2CAA2C;EAC3C,gCAAgC;AAClC;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,SAAS;EACT,eAAe;EACf,iBAAiB;EACjB,2CAA2C;AAC7C;AACA,WAAW,kBAAkB,EAAE,gBAAgB,EAAE,0BAA0B,EAAE;AAC7E,iBAAiB,0BAA0B,EAAE;AAC7C;EACE,+BAA+B;EAC/B,sCAAsC;EACtC,kBAAkB;EAClB,+BAA+B;EAC/B,gBAAgB;EAChB,kBAAkB;EAClB,4CAA4C;AAC9C;AACA,gBAAgB,+BAA+B,EAAE,2BAA2B,EAAE;;AAE9E,sBAAsB,OAAO,UAAU,EAAE,EAAE,KAAK,UAAU,EAAE,EAAE;AAC9D,uBAAuB,OAAO,UAAU,EAAE,2BAA2B,EAAE,EAAE,KAAK,UAAU,EAAE,wBAAwB,EAAE,EAAE;;AAEtH;EACE,iBAAiB,aAAa,EAAE;EAChC,iBAAiB,gBAAgB,EAAE;EACnC,YAAY,uBAAuB,EAAE;AACvC;;AAEA;EACE,+BAA+B;EAC/B,sCAAsC;EACtC,iBAAiB;EACjB,+BAA+B;EAC/B,2BAA2B;EAC3B,6BAA6B;EAC7B,uDAAuD;AACzD;;AAEA;EACE,0CAA0C;EAC1C,2BAA2B;AAC7B;;AAEA,+BAA+B;AAC/B;EACE,kGAAkG;EAClG,2BAA2B;EAC3B,oBAAoB;EACpB,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,QAAQ;EACR,iGAAiG;EACjG,oBAAoB;AACtB;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,SAAS;EACT,oBAAoB,SAAS,6BAA6B;AAC5D;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,mCAAmC;EACnC,yCAAyC;EACzC,4BAA4B;EAC5B,iBAAiB;EACjB,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;EACzB,iBAAiB;EACjB,mBAAmB;EACnB,mBAAmB;AACrB;;AAEA;EACE,WAAW;EACX,UAAU;EACV,WAAW;EACX,iCAAiC;EACjC,kBAAkB;EAClB,4CAA4C;AAC9C;;AAEA;EACE,WAAW,UAAU,EAAE,mBAAmB,EAAE;EAC5C,YAAY,YAAY,EAAE,qBAAqB,EAAE;AACnD;;AAEA;EACE,qCAAqC;EACrC,gBAAgB;EAChB,iBAAiB;EACjB,wBAAwB;EACxB,mBAAmB;AACrB;;AAEA;EACE,kBAAkB;EAClB,4BAA4B;AAC9B;;AAEA;EACE,iBAAiB;EACjB,6BAA6B;EAC7B,gBAAgB;EAChB,mBAAmB;EACnB,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,SAAS;EACT,eAAe;EACf,mBAAmB;AACrB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,iCAAiC;EACjC,0BAA0B;EAC1B,gBAAgB;EAChB,kBAAkB;EAClB,kBAAkB;EAClB,+BAA+B;EAC/B,YAAY;EACZ,eAAe;EACf,6DAA6D;EAC7D,2CAA2C;EAC3C,qBAAqB;AACvB;;AAEA;EACE,mBAAmB;EACnB,2BAA2B;EAC3B,2CAA2C;AAC7C;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,uBAAuB;EACvB,2BAA2B;EAC3B,gBAAgB;EAChB,kBAAkB;EAClB,kBAAkB;EAClB,+BAA+B;EAC/B,0CAA0C;EAC1C,eAAe;EACf,8CAA8C;EAC9C,qBAAqB;AACvB;;AAEA;EACE,mCAAmC;EACnC,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA,0CAA0C;AAC1C;EACE,gBAAgB;EAChB,oBAAoB;EACpB,mBAAmB;EACnB,SAAS;EACT,kCAAkC;EAClC,wCAAwC;EACxC,mBAAmB;EACnB,0BAA0B;EAC1B,kBAAkB;EAClB,6BAA6B;AAC/B;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,iCAAiC;EACjC,kBAAkB;EAClB,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,kBAAkB;EAClB,cAAc;AAChB;;AAEA;EACE,kBAAkB;EAClB,aAAa;EACb,sBAAsB;AACxB;;AAEA,6DAA6D;AAC7D;EACE,SAAO;EACP,+BAA+B;EAC/B,8BAA8B;EAC9B,uCAAuC;EACvC,wFAAwF;EACxF,gBAAgB;EAChB,uBAAuB;EACvB,aAAa;EACb,sBAAsB;EACtB,kBAAkB;AACpB;;AAEA,kEAAkE;AAClE;EACE,SAAO;EACP,aAAa;EACb,sBAAsB;EACtB,kBAAkB;EAClB,gBAAgB;EAChB,6BAA6B;AAC/B;;AAEA,+CAA+C;AAC/C;EACE,SAAO;EACP,aAAa;EACb,sBAAsB;AACxB;;AAEA,0DAA0D;AAC1D;EACE,8EAA8E;EAC9E,2BAA2B;EAC3B,eAAe;EACf,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,SAAS;EACT,eAAe;AACjB;;AAEA;EACE,kBAAkB;EAClB,gBAAgB;AAClB;;AAEA;EACE,kBAAkB;EAClB,YAAY;AACd;;AAEA;EACE,UAAU;EACV,WAAW;EACX,kBAAkB;EAClB,iCAAiC;AACnC;;AAEA,0DAA0D;AAC1D;EACE,eAAe;EACf,gCAAgC;AAClC;;AAEA;EACE,kBAAkB;EAClB,mBAAmB;AACrB;;AAEA;EACE,cAAc;AAChB;;AAEA;EACE,aAAa;EACb,2DAA2D;EAC3D,SAAS;AACX;;AAEA;EACE,gCAAgC;EAChC,uCAAuC;EACvC,+BAA+B;EAC/B,kBAAkB;EAClB,+DAA+D;EAC/D,kBAAkB;EAClB,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,SAAS;EACT,OAAO;EACP,QAAQ;EACR,WAAW;EACX,8EAA8E;EAC9E,oBAAoB;EACpB,sBAAsB;EACtB,0BAA0B;AAC5B;;AAEA;EACE,8BAA8B;EAC9B,2BAA2B;EAC3B,iCAAiC;AACnC;;AAEA,6BAA6B,oBAAoB,EAAE;;AAEnD;EACE,WAAW;EACX,YAAY;EACZ,6EAA6E;EAC7E,+BAA+B;EAC/B,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,mBAAmB;EACnB,iBAAiB;AACnB;;AAEA;EACE,kBAAkB;EAClB,gBAAgB;EAChB,0BAA0B;EAC1B,mBAAmB;AACrB;;AAEA;EACE,kBAAkB;EAClB,0BAA0B;EAC1B,iBAAiB;AACnB;;AAEA,2DAA2D;AAC3D;EACE,+BAA+B;EAC/B,2BAA2B;EAC3B,eAAe;AACjB;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,SAAS;EACT,mBAAmB;AACrB;;AAEA;EACE,qCAAqC;EACrC,gBAAgB;EAChB,gBAAgB;EAChB,mBAAmB;AACrB;;AAEA;EACE,kBAAkB;EAClB,4BAA4B;AAC9B;;AAEA;EACE,eAAe;EACf,6BAA6B;EAC7B,iBAAiB;EACjB,mBAAmB;AACrB;;AAEA;EACE,gBAAgB;EAChB,aAAa;EACb,sBAAsB;EACtB,SAAS;AACX;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,SAAS;EACT,kBAAkB;EAClB,6BAA6B;AAC/B;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,iCAAiC;EACjC,kBAAkB;EAClB,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,iBAAiB;EACjB,0BAA0B;EAC1B,eAAe;AACjB;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,kCAAkC;EAClC,kBAAkB;EAClB,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,iBAAiB;EACjB,4BAA4B;EAC5B,eAAe;AACjB;;AAEA;EACE,kCAAkC;EAClC,wCAAwC;EACxC,+BAA+B;EAC/B,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,kCAAkC;EAClC,kBAAkB;EAClB,gCAAgC;EAChC,8CAA8C;EAC9C,kBAAkB;EAClB,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;EACzB,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,kCAAkC;EAClC,kBAAkB;EAClB,iBAAiB;EACjB,+CAA+C;EAC/C,mBAAmB;AACrB;;AAEA,mCAAmC,mBAAmB,EAAE;AACxD,uCAAuC,kCAAkC,EAAE;;AAE3E,6BAA6B,6BAA6B,EAAE;AAC5D,UAAU,uCAAuC,EAAE,gBAAgB,EAAE;AACrE,UAAU,wCAAwC,EAAE;;AAEpD,2CAA2C;AAC3C;EACE,eAAe;EACf,gCAAgC;AAClC;;AAEA;EACE,kBAAkB;EAClB,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,2DAA2D;EAC3D,SAAS;AACX;;AAEA;EACE,gCAAgC;EAChC,+BAA+B;EAC/B,uCAAuC;EACvC,kBAAkB;EAClB,2CAA2C;EAC3C,4BAA4B;AAC9B;;AAEA,sBAAsB,8BAA8B,EAAE;;AAEtD;EACE,qBAAqB;EACrB,gCAAgC;EAChC,0BAA0B;EAC1B,kBAAkB;EAClB,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;EACzB,iBAAiB;EACjB,mBAAmB;EACnB,mBAAmB;AACrB;;AAEA;EACE,kBAAkB;EAClB,gBAAgB;EAChB,0BAA0B;EAC1B,mBAAmB;AACrB;;AAEA;EACE,iBAAiB;EACjB,0BAA0B;EAC1B,iBAAiB;AACnB;;AAEA,+CAA+C;AAC/C;EACE,eAAe;EACf,gCAAgC;AAClC;;AAEA;EACE,kBAAkB;EAClB,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,SAAS;EACT,oBAAoB;AACtB;;AAEA;EACE,+BAA+B;EAC/B,+BAA+B;EAC/B,gBAAgB;EAChB,8BAA8B;EAC9B,aAAa;EACb,sBAAsB;AACxB;;AAEA;EACE,kBAAkB;EAClB,iEAAiE;EACjE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,kBAAkB;EAClB,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,QAAQ;EACR,0FAA0F;AAC5F;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,iCAAiC;EACjC,kBAAkB;EAClB,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,iBAAiB;EACjB,0BAA0B;EAC1B,uCAAuC;EACvC,sCAAsC;EACtC,eAAe;EACf,YAAY;EACZ,0BAA0B;EAC1B,kBAAkB;EAClB,UAAU;AACZ;;AAEA,kBAAkB,sBAAsB,EAAE;AAC1C,0BAA0B,uCAAuC,EAAE,mBAAmB,EAAE;;AAExF;EACE,OAAO,yCAAyC,EAAE;EAClD,OAAO,wCAAwC,IAAI;EACnD,OAAO,uCAAuC,IAAI;AACpD;;AAEA;EACE,kBAAkB;EAClB,2BAA2B;AAC7B;;AAEA;EACE,eAAe;EACf,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA;EACE,kBAAkB;EAClB,6BAA6B;AAC/B;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,4BAA4B;EAC5B,kBAAkB;EAClB,gBAAgB;EAChB,gBAAgB;EAChB,oBAAoB;AACtB;;AAEA,2BAA2B,SAAS,EAAE;;AAEtC;EACE,gCAAgC;EAChC,uCAAuC;EACvC,+BAA+B;EAC/B,kBAAkB;EAClB,aAAa;EACb,sBAAsB;EACtB,uBAAuB;AACzB;;AAEA;EACE,iBAAiB;EACjB,gBAAgB;EAChB,0BAA0B;EAC1B,mBAAmB;EACnB,gBAAgB;AAClB;;AAEA;EACE,kBAAkB;EAClB,0BAA0B;EAC1B,mBAAmB;EACnB,iBAAiB;AACnB;;AAEA;EACE,aAAa;EACb,SAAS;EACT,eAAe;AACjB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,kBAAkB;EAClB,+BAA+B;EAC/B,kBAAkB;EAClB,gBAAgB;EAChB,eAAe;EACf,6CAA6C;EAC7C,qBAAqB;AACvB;;AAEA,yBAAyB,2BAA2B,EAAE,8BAA8B,EAAE;;AAEtF;EACE,mBAAmB;EACnB,YAAY;AACd;;AAEA;EACE,mBAAmB;EACnB,YAAY;AACd;;AAEA,6CAA6C;AAC7C;EACE,eAAe;EACf,gCAAgC;EAChC,2CAA2C;EAC3C,8CAA8C;AAChD;;AAEA;EACE,kBAAkB;EAClB,kBAAkB;EAClB,gBAAgB;EAChB,qBAAqB;EACrB,yBAAyB;EACzB,0BAA0B;EAC1B,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,eAAe;EACf,SAAS;EACT,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,kBAAkB;EAClB,+BAA+B;EAC/B,6BAA6B;EAC7B,qBAAqB;EACrB,0BAA0B;EAC1B,YAAY;EACZ,wEAAwE;EACxE,eAAe;AACjB;;AAEA;EACE,UAAU;EACV,mCAAmC;EACnC,iCAAiC;EACjC,0BAA0B;AAC5B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,cAAc;AAChB;;AAEA;EACE,kBAAkB;EAClB,gBAAgB;EAChB,mBAAmB;AACrB;;AAEA,uCAAuC;AACvC;EACE,eAAe;EACf,gCAAgC;AAClC;;AAEA;EACE,kBAAkB;EAClB,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,qCAAqC;EACrC,SAAS;EACT,wBAAwB;AAC1B;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,SAAS;EACT,yBAAyB;EACzB,0BAA0B;EAC1B,WAAW;EACX,8EAA8E;EAC9E,UAAU;AACZ;;AAEA;EACE,kBAAkB;EAClB,kBAAkB;EAClB,gCAAgC;EAChC,+BAA+B;EAC/B,uCAAuC;EACvC,kBAAkB;EAClB,UAAU;AACZ;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,+EAA+E;EAC/E,YAAY;EACZ,gBAAgB;EAChB,iBAAiB;EACjB,kBAAkB;EAClB,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,eAAe;EACf,gBAAgB;EAChB,0BAA0B;EAC1B,mBAAmB;AACrB;;AAEA;EACE,kBAAkB;EAClB,0BAA0B;EAC1B,iBAAiB;AACnB;;AAEA,kFAAkF;AAClF;EACE,gCAAgC;EAChC,iBAAiB;AACnB;;AAEA,0DAA0D;AAC1D;EACE,aAAa;EACb,yCAAyC;EACzC,cAAc;EACd,uBAAuB;EACvB,oBAAoB;AACtB;;AAEA,uBAAuB;AACvB;EACE,aAAa;EACb,sBAAsB;EACtB,SAAS;AACX;;AAEA;EACE,YAAY;EACZ,mBAAmB;EACnB,cAAc;EACd,YAAY;EACZ,aAAa;EACb,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA;EACE,kBAAkB;EAClB,0BAA0B;EAC1B,iBAAiB;EACjB,SAAS;AACX;;AAEA,0BAA0B;AAC1B;EACE,iBAAiB;EACjB,gBAAgB;EAChB,cAAc;EACd,sBAAsB;EACtB,yBAAyB;EACzB,mBAAmB;AACrB;;AAEA,6BAA6B;AAC7B;EACE,gBAAgB;EAChB,UAAU;EACV,SAAS;EACT,aAAa;EACb,sBAAsB;EACtB,SAAS;AACX;;AAEA;EACE,iBAAiB;EACjB,0BAA0B;EAC1B,qBAAqB;EACrB,sBAAsB;EACtB,iBAAiB;AACnB;;AAEA,2BAA2B,cAAc,EAAE;;AAE3C,yBAAyB;AACzB;EACE,gBAAgB;EAChB,UAAU;EACV,SAAS;EACT,aAAa;EACb,sBAAsB;EACtB,SAAS;AACX;;AAEA;EACE,kBAAkB;EAClB,iBAAiB;EACjB,0BAA0B;AAC5B;;AAEA;EACE,cAAc;EACd,gBAAgB;EAChB,iBAAiB;AACnB;;AAEA;EACE,0BAA0B;EAC1B,qBAAqB;EACrB,sBAAsB;AACxB;;AAEA,+BAA+B,cAAc,EAAE;;AAE/C,wBAAwB;AACxB;EACE,aAAa;EACb,sBAAsB;EACtB,SAAS;AACX;;AAEA;EACE,aAAa;EACb,SAAS;EACT,eAAe;AACjB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,kCAAkC;EAClC,0BAA0B;EAC1B,qBAAqB;EACrB,2CAA2C;AAC7C;;AAEA;EACE,mBAAmB;EACnB,2BAA2B;AAC7B;;AAEA,qBAAqB;AACrB;EACE;IACE,8BAA8B;IAC9B,cAAc;EAChB;AACF;;AAEA;EACE;IACE,0BAA0B;IAC1B,SAAS;EACX;EACA,uBAAuB,QAAQ,EAAE;AACnC;;AAEA,qCAAqC;AACrC;EACE;;;IAGE,0BAA0B;IAC1B,SAAS;EACX;;EAEA,eAAe,aAAa,EAAE;;EAE9B;IACE,0BAA0B;EAC5B;;EAEA,qBAAqB,aAAa,EAAE;;EAEpC,aAAa,aAAa,EAAE;;EAE5B,oBAAoB,kBAAkB,EAAE;AAC1C;;AAEA;EACE,QAAQ,oBAAoB,EAAE;EAC9B,WAAW,iBAAiB,EAAE;EAC9B,iBAAiB,0BAA0B,EAAE;EAC7C,iBAAiB,0BAA0B,EAAE;EAC7C,+BAA+B,sBAAsB,EAAE,QAAQ,EAAE;EACjE,gBAAgB,aAAa,EAAE;AACjC;;AAEA,iEAAiE;AACjE;EACE,OAAO,4BAA4B,EAAE,UAAU,EAAE;EACjD,OAAO,UAAU,EAAE;EACnB,OAAO,UAAU,EAAE;EACnB,OAAO,2BAA2B,GAAG,UAAU,EAAE;AACnD;;AAEA;EACE,OAAO,4BAA4B,EAAE,UAAU,EAAE;EACjD,OAAO,UAAU,EAAE;EACnB,OAAO,UAAU,EAAE;EACnB,OAAO,2BAA2B,GAAG,UAAU,EAAE;AACnD;;AAEA,qEAAqE;;AAErE,oDAAoD;AACpD;EACE,qBAAqB;EACrB,sBAAsB;AACxB;AACA;EACE,wCAAwC;AAC1C;AACA;EACE,8CAA8C;AAChD;;AAEA;EACE,KAAK,oBAAoB,EAAE;AAC7B;;AAEA,yDAAyD;AACzD;EACE,OAAO,UAAU,EAAE,qBAAqB,EAAE;EAC1C,OAAO,UAAU,EAAE,sBAAsB,EAAE;EAC3C,OAAO,UAAU,EAAE,mBAAmB,EAAE;AAC1C;;AAEA,wBAAwB;AACxB;EACE,WAAW,UAAU,EAAE;EACvB,YAAY,YAAY,EAAE;AAC5B;;AAEA,kCAAkC;AAClC;EACE,kBAAkB;EAClB,kBAAkB;EAClB,mCAAmC;EACnC,oBAAoB;EACpB,wFAAwF;AAC1F;;AAEA;EACE,OAAO,qCAAqC,KAAK,aAAa,EAAE;EAChE,OAAO,wCAAwC,EAAE,aAAa,EAAE;EAChE,OAAO,uCAAuC,GAAG,aAAa,EAAE;AAClE;;AAEA;EACE,wBAAwB,oBAAoB,EAAE,eAAe,EAAE;EAC/D,wBAAwB,eAAe,EAAE;AAC3C;;AAEA,wDAAwD;AACxD;+DAC+D;AAC/D;EACE,aAAa;EACb,gBAAgB;AAClB;;AAEA,4CAA4C;AAC5C;EACE;;;IAGE,UAAU;IACV,2BAA2B;IAC3B,uFAAuF;EACzF;;EAEA;;;IAGE,UAAU;IACV,wBAAwB;EAC1B;AACF;;AAEA,2EAA2E;AAC3E,wEAAwE;AACxE;EACE,qCAAqC;EACrC,SAAS;AACX;;AAEA,iCAAiC,kBAAkB,EAAE;;AAErD,oCAAoC,kBAAkB,EAAE;;AAExD;EACE,gCAAgC;EAChC,uCAAuC;EACvC,+BAA+B;EAC/B,uBAAuB;EACvB,mBAAmB;EACnB,8CAA8C;AAChD;;AAEA,qBAAqB,gBAAgB,EAAE,mBAAmB,EAAE;;AAE5D;EACE,kBAAkB;EAClB,gBAAgB;EAChB,0BAA0B;EAC1B,kBAAkB;AACpB;;AAEA;EACE,kBAAkB;EAClB,0BAA0B;EAC1B,iBAAiB;AACnB;;AAEA;EACE,aAAa;EACb,qCAAqC;EACrC,MAAM;EACN,2CAA2C;AAC7C;;AAEA;EACE,wBAAwB;EACxB,kBAAkB;EAClB,6CAA6C;AAC/C;;AAEA,2BAA2B,kBAAkB,EAAE,eAAe,EAAE,gBAAgB,EAAE;;AAElF;EACE,kBAAkB;EAClB,gBAAgB;EAChB,0BAA0B;EAC1B,kBAAkB;AACpB;;AAEA;EACE,kBAAkB;EAClB,0BAA0B;EAC1B,iBAAiB;EACjB,mBAAmB;AACrB;;AAEA;EACE,gBAAgB;EAChB,gBAAgB;EAChB,UAAU;EACV,aAAa;EACb,eAAe;EACf,QAAQ;AACV;;AAEA;EACE,mCAAmC;EACnC,0BAA0B;EAC1B,kBAAkB;EAClB,gBAAgB;EAChB,iBAAiB;EACjB,mBAAmB;AACrB;;AAEA;EACE,iCAAiC,qCAAqC,EAAE;EACxE,eAAe,qCAAqC,EAAE;EACtD,6BAA6B,kBAAkB,EAAE,eAAe,EAAE,gBAAgB,EAAE;EACpF,+BAA+B,2CAA2C,EAAE;EAC5E,6BAA6B,mBAAmB,EAAE;AACpD;;AAEA;EACE,iCAAiC,0BAA0B,EAAE;EAC7D,gBAAgB,uBAAuB,EAAE;EACzC,eAAe,0BAA0B,EAAE;EAC3C;;IAEE,kBAAkB,EAAE,eAAe,EAAE,gBAAgB;EACvD;EACA,+BAA+B,2CAA2C,EAAE;AAC9E","sourcesContent":["/* ============================================================\n   KinetiQ (openMKM) Landing Page — Full Stylesheet\n   Aligned to Sagar's requirements:\n   - Standalone product page feel\n   - Value proposition + key benefits\n   - Web-based differentiator section\n   - Video / social media section\n   - Contact section with social link slots\n   Inspired by: kintekexplorer.com structure\n   ============================================================ */\n\n/* ---------- Reset & Base ---------- */\n*, *::before, *::after {\n  box-sizing: border-box;\n  margin: 0;\n  padding: 0;\n}\n\n:root {\n  --kinetic-blue:     #1A3C6E;\n  --kinetic-teal:     #0B8DA6;\n  --kinetic-accent:   #00C2A8;\n  --kinetic-light:    #faf6f0;\n  --kinetic-white:    #FFFFFF;\n  --kinetic-dark:     #0D1F35;\n  --kinetic-gray:     #6B7A8D;\n  --kinetic-border:   #D6E4EC;\n  --kinetic-warn:     #F4A261;\n\n  --font-display: 'Inter', 'Segoe UI', sans-serif;\n  --font-body:    'Inter', 'Segoe UI', sans-serif;\n\n  --radius-sm: 6px;\n  --radius-md: 12px;\n  --radius-lg: 20px;\n\n  --shadow-card: 0 4px 24px rgba(10, 50, 90, 0.10);\n  --shadow-hero: 0 8px 40px rgba(10, 50, 90, 0.18);\n}\n\nhtml { scroll-behavior: smooth; }\n\nbody {\n  font-family: var(--font-body);\n  color: var(--kinetic-dark);\n  background: var(--kinetic-white);\n  line-height: 1.65;\n  -webkit-font-smoothing: antialiased;\n}\n\na { color: inherit; text-decoration: none; }\nimg { max-width: 100%; display: block; }\n\n/* ---------- Utility ---------- */\n.container {\n  max-width: 1140px;\n  margin: 0 auto;\n  padding: 0 24px;\n}\n\n.section-label {\n  display: inline-block;\n  font-size: 0.75rem;\n  font-weight: 700;\n  letter-spacing: 0.12em;\n  text-transform: uppercase;\n  color: var(--kinetic-teal);\n  margin-bottom: 12px;\n}\n\n.section-title {\n  font-size: clamp(1.75rem, 3.5vw, 2.5rem);\n  font-weight: 700;\n  color: var(--kinetic-blue);\n  line-height: 1.2;\n  margin-bottom: 16px;\n}\n\n.section-subtitle {\n  font-size: 1.05rem;\n  color: var(--kinetic-gray);\n  max-width: 620px;\n  line-height: 1.7;\n}\n\n/* ---------- NAVIGATION ---------- */\n.nav {\n  position: sticky;\n  top: 0;\n  z-index: 100;\n  background: var(--kinetic-white);\n  border-bottom: 1px solid var(--kinetic-border);\n  box-shadow: 0 2px 12px rgba(26, 60, 110, 0.06);\n}\n\n.nav-inner {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  height: 68px;\n  gap: 24px;\n  /* Edge-to-edge, not the centered 1140px .container used by page\n     sections — that centering was leaving a large empty gutter before\n     the logo on wider screens. A generous max-width keeps it from\n     stretching absurdly on ultra-wide monitors while sitting flush\n     near the true edges everywhere realistic. */\n  max-width: 1600px;\n  margin: 0 auto;\n  padding: 0 32px;\n}\n\n.nav-logo {\n  display: flex;\n  align-items: center;\n  gap: 10px;\n  font-size: 1.35rem;\n  font-weight: 800;\n  color: var(--kinetic-blue);\n  letter-spacing: -0.02em;\n  white-space: nowrap;\n  flex-shrink: 0;\n}\n\n.nav-logo span {\n  color: var(--kinetic-teal);\n}\n\n.nav-logo-img {\n  max-height: 50px;\n  width: auto;\n  object-fit: contain;\n  object-position: left center;\n  display: block;\n  border: none;\n  outline: none;\n  box-shadow: none;\n  background: transparent;\n}\n\n.nav-links {\n  display: flex;\n  align-items: center;\n  gap: 32px;\n  list-style: none;\n}\n\n.nav-links a {\n  font-size: 0.92rem;\n  font-weight: 500;\n  color: var(--kinetic-gray);\n  transition: color 0.2s;\n}\n\n.nav-links a:hover { color: var(--kinetic-teal); }\n\n/* Active tab (scroll-spy): the tab for the section currently in view.\n   The transparent border on every tab keeps the row from shifting when one is active. */\n.nav-links a:not(.nav-help-link) {\n  border-bottom: 2px solid transparent;\n  padding: 4px 0;\n}\n\n.nav-links a.nav-link-active:not(.nav-help-link) {\n  color: var(--kinetic-teal);\n  font-weight: 700;\n  border-bottom-color: var(--kinetic-teal);\n}\n\n/* \"How to Register\" — visually distinct from the scroll-links so it reads\n   as help/support, not another page section. Small pill + icon keeps it\n   minimal rather than adding a second full-weight CTA next to Login. */\n.nav-help-link {\n  display: flex;\n  align-items: center;\n  gap: 6px;\n  font-size: 0.86rem !important;\n  font-weight: 600 !important;\n  color: var(--kinetic-teal) !important;\n  padding: 6px 12px;\n  border: 1px solid var(--kinetic-border);\n  border-radius: 999px;\n  white-space: nowrap;\n  transition: background 0.2s, border-color 0.2s;\n}\n.nav-help-link:hover {\n  background: rgba(11, 141, 166, 0.08);\n  border-color: var(--kinetic-teal);\n}\n.nav-help-icon {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 16px;\n  height: 16px;\n  border-radius: 50%;\n  background: var(--kinetic-teal);\n  color: var(--kinetic-white);\n  font-size: 0.7rem;\n  font-weight: 800;\n  flex-shrink: 0;\n}\n\n/* ---------- \"How to Register\" quick-help modal ---------- */\n.rh-overlay {\n  position: fixed;\n  inset: 0;\n  background: rgba(13, 31, 53, 0.55);\n  backdrop-filter: blur(2px);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  padding: 24px;\n  z-index: 1000;\n  animation: rhFadeIn 0.18s ease;\n}\n\n.rh-modal {\n  position: relative;\n  width: 100%;\n  max-width: 560px;\n  max-height: 88vh;\n  overflow-y: auto;\n  background: var(--kinetic-white);\n  border-radius: var(--radius-lg);\n  box-shadow: var(--shadow-hero);\n  padding: 32px 32px 28px;\n  animation: rhSlideUp 0.2s ease;\n}\n\n.rh-close {\n  position: absolute;\n  top: 16px;\n  right: 16px;\n  width: 32px;\n  height: 32px;\n  border: none;\n  background: var(--kinetic-light);\n  color: var(--kinetic-gray);\n  border-radius: 50%;\n  font-size: 1.3rem;\n  line-height: 1;\n  cursor: pointer;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  transition: background 0.15s, color 0.15s;\n}\n.rh-close:hover { background: var(--kinetic-border); color: var(--kinetic-dark); }\n\n.rh-header { margin-bottom: 20px; padding-right: 28px; }\n.rh-tag {\n  display: inline-block;\n  font-size: 0.7rem;\n  font-weight: 700;\n  letter-spacing: 0.1em;\n  text-transform: uppercase;\n  color: var(--kinetic-teal);\n  margin-bottom: 8px;\n}\n.rh-header h3 {\n  font-size: 1.4rem;\n  font-weight: 800;\n  color: var(--kinetic-blue);\n  margin-bottom: 6px;\n}\n.rh-header p { color: var(--kinetic-gray); font-size: 0.92rem; }\n\n.rh-steps { display: flex; flex-direction: column; gap: 18px; margin-bottom: 22px; }\n\n.rh-step { display: flex; gap: 14px; }\n\n.rh-step-num {\n  flex-shrink: 0;\n  width: 26px;\n  height: 26px;\n  border-radius: 50%;\n  background: var(--kinetic-blue);\n  color: var(--kinetic-white);\n  font-size: 0.82rem;\n  font-weight: 700;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}\n\n.rh-step-body { flex: 1; min-width: 0; }\n.rh-step-title { font-weight: 700; color: var(--kinetic-dark); margin-bottom: 3px; font-size: 0.95rem; }\n.rh-step-body p { font-size: 0.88rem; color: var(--kinetic-gray); line-height: 1.55; }\n.rh-step-body strong { color: var(--kinetic-dark); }\n\n.rh-shot {\n  margin-top: 10px;\n  border: 1px solid var(--kinetic-border);\n  border-radius: var(--radius-md);\n  overflow: hidden;\n  background: var(--kinetic-light);\n}\n.rh-shot img { width: 100%; display: block; }\n.rh-shot-note {\n  display: block;\n  font-size: 0.74rem;\n  color: var(--kinetic-gray);\n  padding: 6px 10px;\n  border-top: 1px solid var(--kinetic-border);\n  background: var(--kinetic-white);\n}\n\n.rh-footer {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 12px;\n  flex-wrap: wrap;\n  padding-top: 16px;\n  border-top: 1px solid var(--kinetic-border);\n}\n.rh-link { font-size: 0.85rem; font-weight: 600; color: var(--kinetic-teal); }\n.rh-link:hover { text-decoration: underline; }\n.rh-cta {\n  background: var(--kinetic-teal);\n  color: var(--kinetic-white) !important;\n  padding: 10px 20px;\n  border-radius: var(--radius-sm);\n  font-weight: 600;\n  font-size: 0.88rem;\n  transition: background 0.2s, transform 0.15s;\n}\n.rh-cta:hover { background: var(--kinetic-blue); transform: translateY(-1px); }\n\n@keyframes rhFadeIn { from { opacity: 0; } to { opacity: 1; } }\n@keyframes rhSlideUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }\n\n@media (max-width: 640px) {\n  .nav-help-text { display: none; }\n  .nav-help-link { padding: 6px 8px; }\n  .rh-modal { padding: 24px 20px 20px; }\n}\n\n.nav-cta {\n  background: var(--kinetic-teal);\n  color: var(--kinetic-white) !important;\n  padding: 9px 22px;\n  border-radius: var(--radius-sm);\n  font-weight: 600 !important;\n  font-size: 0.88rem !important;\n  transition: background 0.2s, transform 0.15s !important;\n}\n\n.nav-cta:hover {\n  background: var(--kinetic-blue) !important;\n  transform: translateY(-1px);\n}\n\n/* ---------- HERO ---------- */\n.hero {\n  background: linear-gradient(160deg, var(--kinetic-blue) 0%, #0E2A52 55%, var(--kinetic-dark) 100%);\n  color: var(--kinetic-white);\n  padding: 96px 0 80px;\n  overflow: hidden;\n  position: relative;\n}\n\n.hero::before {\n  content: '';\n  position: absolute;\n  inset: 0;\n  background: radial-gradient(ellipse 70% 60% at 75% 40%, rgba(0,194,168,0.12) 0%, transparent 70%);\n  pointer-events: none;\n}\n\n.hero-inner {\n  display: grid;\n  grid-template-columns: 1fr 1fr;\n  gap: 64px;\n  align-items: stretch;        /* both columns same height */\n}\n\n.hero-badge {\n  display: inline-flex;\n  align-items: center;\n  gap: 8px;\n  background: rgba(0, 194, 168, 0.15);\n  border: 1px solid rgba(0, 194, 168, 0.35);\n  color: var(--kinetic-accent);\n  font-size: 0.8rem;\n  font-weight: 600;\n  letter-spacing: 0.06em;\n  text-transform: uppercase;\n  padding: 6px 14px;\n  border-radius: 99px;\n  margin-bottom: 24px;\n}\n\n.hero-badge::before {\n  content: '';\n  width: 7px;\n  height: 7px;\n  background: var(--kinetic-accent);\n  border-radius: 50%;\n  animation: pulse-dot 2s ease-in-out infinite;\n}\n\n@keyframes pulse-dot {\n  0%, 100% { opacity: 1; transform: scale(1); }\n  50%       { opacity: 0.5; transform: scale(1.4); }\n}\n\n.hero h1 {\n  font-size: clamp(2rem, 4.5vw, 3.2rem);\n  font-weight: 800;\n  line-height: 1.12;\n  letter-spacing: -0.025em;\n  margin-bottom: 20px;\n}\n\n.hero h1 em {\n  font-style: normal;\n  color: var(--kinetic-accent);\n}\n\n.hero-description {\n  font-size: 1.1rem;\n  color: rgba(255,255,255,0.78);\n  line-height: 1.7;\n  margin-bottom: 36px;\n  max-width: 480px;\n}\n\n.hero-actions {\n  display: flex;\n  gap: 14px;\n  flex-wrap: wrap;\n  align-items: center;\n}\n\n.btn-primary {\n  display: inline-flex;\n  align-items: center;\n  gap: 8px;\n  background: var(--kinetic-accent);\n  color: var(--kinetic-dark);\n  font-weight: 700;\n  font-size: 0.95rem;\n  padding: 14px 28px;\n  border-radius: var(--radius-sm);\n  border: none;\n  cursor: pointer;\n  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;\n  box-shadow: 0 4px 16px rgba(0,194,168,0.35);\n  text-decoration: none;\n}\n\n.btn-primary:hover {\n  background: #00a896;\n  transform: translateY(-2px);\n  box-shadow: 0 6px 24px rgba(0,194,168,0.45);\n}\n\n.btn-secondary {\n  display: inline-flex;\n  align-items: center;\n  gap: 8px;\n  background: transparent;\n  color: var(--kinetic-white);\n  font-weight: 600;\n  font-size: 0.95rem;\n  padding: 13px 26px;\n  border-radius: var(--radius-sm);\n  border: 1.5px solid rgba(255,255,255,0.35);\n  cursor: pointer;\n  transition: border-color 0.2s, background 0.2s;\n  text-decoration: none;\n}\n\n.btn-secondary:hover {\n  border-color: var(--kinetic-accent);\n  background: rgba(0,194,168,0.1);\n  color: var(--kinetic-accent);\n}\n\n/* Web-based differentiator pill in hero */\n.hero-web-pill {\n  margin-top: 28px;\n  display: inline-flex;\n  align-items: center;\n  gap: 10px;\n  background: rgba(255,255,255,0.07);\n  border: 1px solid rgba(255,255,255,0.15);\n  border-radius: 99px;\n  padding: 8px 16px 8px 10px;\n  font-size: 0.82rem;\n  color: rgba(255,255,255,0.70);\n}\n\n.hero-web-pill .pill-icon {\n  width: 26px;\n  height: 26px;\n  background: var(--kinetic-accent);\n  border-radius: 50%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  font-size: 0.85rem;\n  flex-shrink: 0;\n}\n\n.hero-visual {\n  position: relative;\n  display: flex;\n  flex-direction: column;\n}\n\n/* Outer card — exactly as tall as the text column, no more */\n.hero-carousel-shell {\n  flex: 1;\n  border-radius: var(--radius-md);\n  box-shadow: var(--shadow-hero);\n  border: 1px solid rgba(255,255,255,0.1);\n  background: linear-gradient(150deg, rgba(11,141,166,0.20) 0%, rgba(0,194,168,0.07) 100%);\n  overflow: hidden;\n  padding: 22px 22px 16px;\n  display: flex;\n  flex-direction: column;\n  position: relative;\n}\n\n/* Carousel wrapper — stretches to fill shell with zero leftover */\n.hero-carousel {\n  flex: 1;\n  display: flex;\n  flex-direction: column;\n  position: relative;\n  overflow: hidden;\n  color: rgba(255,255,255,0.92);\n}\n\n/* Every slide uses the full available height */\n.hero-carousel > div:first-child {\n  flex: 1;\n  display: flex;\n  flex-direction: column;\n}\n\n/* ---------- WEB-BASED DIFFERENTIATOR BANNER ---------- */\n.differentiator-banner {\n  background: linear-gradient(90deg, var(--kinetic-teal), var(--kinetic-accent));\n  color: var(--kinetic-white);\n  padding: 18px 0;\n  text-align: center;\n}\n\n.differentiator-banner-inner {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  gap: 12px;\n  flex-wrap: wrap;\n}\n\n.differentiator-banner strong {\n  font-size: 0.95rem;\n  font-weight: 700;\n}\n\n.differentiator-banner span {\n  font-size: 0.92rem;\n  opacity: 0.9;\n}\n\n.diff-divider {\n  width: 4px;\n  height: 4px;\n  border-radius: 50%;\n  background: rgba(255,255,255,0.5);\n}\n\n/* ---------- VALUE PROPOSITION / WHY KINETIC ---------- */\n.why-section {\n  padding: 88px 0;\n  background: var(--kinetic-white);\n}\n\n.why-header {\n  text-align: center;\n  margin-bottom: 60px;\n}\n\n.why-header .section-subtitle {\n  margin: 0 auto;\n}\n\n.benefits-grid {\n  display: grid;\n  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));\n  gap: 28px;\n}\n\n.benefit-card {\n  background: var(--kinetic-light);\n  border: 1px solid var(--kinetic-border);\n  border-radius: var(--radius-md);\n  padding: 32px 28px;\n  transition: box-shadow 0.25s, transform 0.2s, border-color 0.2s;\n  position: relative;\n  overflow: hidden;\n}\n\n.benefit-card::after {\n  content: '';\n  position: absolute;\n  bottom: 0;\n  left: 0;\n  right: 0;\n  height: 3px;\n  background: linear-gradient(90deg, var(--kinetic-teal), var(--kinetic-accent));\n  transform: scaleX(0);\n  transform-origin: left;\n  transition: transform 0.3s;\n}\n\n.benefit-card:hover {\n  box-shadow: var(--shadow-card);\n  transform: translateY(-4px);\n  border-color: var(--kinetic-teal);\n}\n\n.benefit-card:hover::after { transform: scaleX(1); }\n\n.benefit-icon {\n  width: 52px;\n  height: 52px;\n  background: linear-gradient(135deg, var(--kinetic-blue), var(--kinetic-teal));\n  border-radius: var(--radius-sm);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  margin-bottom: 20px;\n  font-size: 1.4rem;\n}\n\n.benefit-card h3 {\n  font-size: 1.08rem;\n  font-weight: 700;\n  color: var(--kinetic-blue);\n  margin-bottom: 10px;\n}\n\n.benefit-card p {\n  font-size: 0.92rem;\n  color: var(--kinetic-gray);\n  line-height: 1.65;\n}\n\n/* ---------- WEB-BASED DIFFERENTIATOR SECTION ---------- */\n.web-advantage {\n  background: var(--kinetic-blue);\n  color: var(--kinetic-white);\n  padding: 88px 0;\n}\n\n.web-advantage-inner {\n  display: grid;\n  grid-template-columns: 1fr 1fr;\n  gap: 72px;\n  align-items: center;\n}\n\n.web-advantage-content h2 {\n  font-size: clamp(1.6rem, 3vw, 2.2rem);\n  font-weight: 800;\n  line-height: 1.2;\n  margin-bottom: 20px;\n}\n\n.web-advantage-content h2 em {\n  font-style: normal;\n  color: var(--kinetic-accent);\n}\n\n.web-advantage-content p {\n  font-size: 1rem;\n  color: rgba(255,255,255,0.75);\n  line-height: 1.75;\n  margin-bottom: 28px;\n}\n\n.comparison-list {\n  list-style: none;\n  display: flex;\n  flex-direction: column;\n  gap: 14px;\n}\n\n.comparison-list li {\n  display: flex;\n  align-items: flex-start;\n  gap: 12px;\n  font-size: 0.95rem;\n  color: rgba(255,255,255,0.85);\n}\n\n.comparison-list li .check {\n  width: 22px;\n  height: 22px;\n  background: var(--kinetic-accent);\n  border-radius: 50%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  flex-shrink: 0;\n  font-size: 0.7rem;\n  color: var(--kinetic-dark);\n  margin-top: 2px;\n}\n\n.comparison-list li .cross {\n  width: 22px;\n  height: 22px;\n  background: rgba(255,255,255,0.12);\n  border-radius: 50%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  flex-shrink: 0;\n  font-size: 0.7rem;\n  color: rgba(255,255,255,0.4);\n  margin-top: 2px;\n}\n\n.comparison-table {\n  background: rgba(255,255,255,0.06);\n  border: 1px solid rgba(255,255,255,0.12);\n  border-radius: var(--radius-md);\n  overflow: hidden;\n}\n\n.comparison-table-header {\n  display: grid;\n  grid-template-columns: 2fr 1fr 1fr;\n  padding: 14px 20px;\n  background: rgba(0,194,168,0.15);\n  border-bottom: 1px solid rgba(255,255,255,0.1);\n  font-size: 0.78rem;\n  font-weight: 700;\n  letter-spacing: 0.08em;\n  text-transform: uppercase;\n  color: var(--kinetic-accent);\n}\n\n.comparison-table-row {\n  display: grid;\n  grid-template-columns: 2fr 1fr 1fr;\n  padding: 13px 20px;\n  font-size: 0.9rem;\n  border-bottom: 1px solid rgba(255,255,255,0.06);\n  align-items: center;\n}\n\n.comparison-table-row:last-child { border-bottom: none; }\n.comparison-table-row:nth-child(odd) { background: rgba(255,255,255,0.03); }\n\n.comparison-table-row span { color: rgba(255,255,255,0.75); }\n.ct-yes { color: var(--kinetic-accent) !important; font-weight: 600; }\n.ct-no  { color: rgba(255,255,255,0.25) !important; }\n\n/* ---------- FEATURES SECTION ---------- */\n.features-section {\n  padding: 88px 0;\n  background: var(--kinetic-light);\n}\n\n.features-header {\n  text-align: center;\n  margin-bottom: 60px;\n}\n\n.features-grid {\n  display: grid;\n  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));\n  gap: 24px;\n}\n\n.feature-card {\n  background: var(--kinetic-white);\n  border-radius: var(--radius-md);\n  border: 1px solid var(--kinetic-border);\n  padding: 30px 26px;\n  box-shadow: 0 2px 10px rgba(26,60,110,0.05);\n  transition: box-shadow 0.25s;\n}\n\n.feature-card:hover { box-shadow: var(--shadow-card); }\n\n.feature-tag {\n  display: inline-block;\n  background: rgba(11,141,166,0.1);\n  color: var(--kinetic-teal);\n  font-size: 0.72rem;\n  font-weight: 700;\n  letter-spacing: 0.09em;\n  text-transform: uppercase;\n  padding: 3px 10px;\n  border-radius: 99px;\n  margin-bottom: 14px;\n}\n\n.feature-card h3 {\n  font-size: 1.05rem;\n  font-weight: 700;\n  color: var(--kinetic-blue);\n  margin-bottom: 10px;\n}\n\n.feature-card p {\n  font-size: 0.9rem;\n  color: var(--kinetic-gray);\n  line-height: 1.65;\n}\n\n/* ---------- DEMO / VIDEO SECTION ---------- */\n.demo-section {\n  padding: 88px 0;\n  background: var(--kinetic-white);\n}\n\n.demo-section-header {\n  text-align: center;\n  margin-bottom: 52px;\n}\n\n.demo-grid {\n  display: grid;\n  grid-template-columns: 1fr 1fr;\n  gap: 32px;\n  align-items: stretch;\n}\n\n.demo-video-card {\n  background: var(--kinetic-dark);\n  border-radius: var(--radius-md);\n  overflow: hidden;\n  box-shadow: var(--shadow-hero);\n  display: flex;\n  flex-direction: column;\n}\n\n.demo-video-thumb {\n  aspect-ratio: 16/9;\n  background: linear-gradient(135deg, var(--kinetic-blue), #0a2240);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  position: relative;\n  cursor: pointer;\n  overflow: hidden;\n}\n\n.demo-video-thumb::before {\n  content: '';\n  position: absolute;\n  inset: 0;\n  background: radial-gradient(ellipse 60% 50% at 50% 50%, rgba(0,194,168,0.15), transparent);\n}\n\n.play-btn {\n  width: 72px;\n  height: 72px;\n  background: var(--kinetic-accent);\n  border-radius: 50%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  font-size: 1.6rem;\n  color: var(--kinetic-dark);\n  box-shadow: 0 0 0 0 rgba(0,194,168,0.4);\n  animation: ripple 2s ease-out infinite;\n  cursor: pointer;\n  border: none;\n  transition: transform 0.2s;\n  position: relative;\n  z-index: 1;\n}\n\n.play-btn:hover { transform: scale(1.08); }\n.play-btn:focus-visible { outline: 2px solid var(--kinetic-white); outline-offset: 3px; }\n\n@keyframes ripple {\n  0%   { box-shadow: 0 0 0 0   rgba(0,194,168,0.4); }\n  70%  { box-shadow: 0 0 0 22px rgba(0,194,168,0);   }\n  100% { box-shadow: 0 0 0 0   rgba(0,194,168,0);   }\n}\n\n.demo-video-info {\n  padding: 20px 22px;\n  color: var(--kinetic-white);\n}\n\n.demo-video-info h3 {\n  font-size: 1rem;\n  font-weight: 700;\n  margin-bottom: 6px;\n}\n\n.demo-video-info p {\n  font-size: 0.85rem;\n  color: rgba(255,255,255,0.55);\n}\n\n.demo-video-info a {\n  display: inline-flex;\n  align-items: center;\n  gap: 6px;\n  color: var(--kinetic-accent);\n  font-size: 0.85rem;\n  font-weight: 600;\n  margin-top: 12px;\n  transition: gap 0.2s;\n}\n\n.demo-video-info a:hover { gap: 10px; }\n\n.demo-cta-card {\n  background: var(--kinetic-light);\n  border: 1px solid var(--kinetic-border);\n  border-radius: var(--radius-md);\n  padding: 40px 36px;\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n}\n\n.demo-cta-card h3 {\n  font-size: 1.4rem;\n  font-weight: 700;\n  color: var(--kinetic-blue);\n  margin-bottom: 14px;\n  line-height: 1.3;\n}\n\n.demo-cta-card p {\n  font-size: 0.95rem;\n  color: var(--kinetic-gray);\n  margin-bottom: 28px;\n  line-height: 1.65;\n}\n\n.social-links-row {\n  display: flex;\n  gap: 12px;\n  flex-wrap: wrap;\n}\n\n.social-link-btn {\n  display: inline-flex;\n  align-items: center;\n  gap: 8px;\n  padding: 10px 20px;\n  border-radius: var(--radius-sm);\n  font-size: 0.88rem;\n  font-weight: 600;\n  cursor: pointer;\n  transition: transform 0.15s, box-shadow 0.15s;\n  text-decoration: none;\n}\n\n.social-link-btn:hover { transform: translateY(-2px); box-shadow: var(--shadow-card); }\n\n.social-link-btn.youtube {\n  background: #FF0000;\n  color: white;\n}\n\n.social-link-btn.linkedin {\n  background: #0A66C2;\n  color: white;\n}\n\n/* ---------- TRUSTED BY (logos) ---------- */\n.trusted-section {\n  padding: 64px 0;\n  background: var(--kinetic-light);\n  border-top: 1px solid var(--kinetic-border);\n  border-bottom: 1px solid var(--kinetic-border);\n}\n\n.trusted-header {\n  text-align: center;\n  font-size: 0.82rem;\n  font-weight: 700;\n  letter-spacing: 0.1em;\n  text-transform: uppercase;\n  color: var(--kinetic-gray);\n  margin-bottom: 36px;\n}\n\n.logos-row {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 32px;\n  justify-content: center;\n  align-items: center;\n}\n\n.logo-placeholder {\n  display: inline-flex;\n  align-items: center;\n  gap: 8px;\n  padding: 10px 18px;\n  border-radius: var(--radius-sm);\n  border: 1px solid transparent;\n  text-decoration: none;\n  color: var(--kinetic-gray);\n  opacity: 0.7;\n  transition: opacity 0.2s, border-color 0.2s, background 0.2s, color 0.2s;\n  cursor: pointer;\n}\n\n.logo-placeholder:hover {\n  opacity: 1;\n  border-color: var(--kinetic-border);\n  background: rgba(255,255,255,0.6);\n  color: var(--kinetic-navy);\n}\n\n.logo-placeholder-icon {\n  display: flex;\n  align-items: center;\n  flex-shrink: 0;\n}\n\n.logo-placeholder-name {\n  font-size: 0.88rem;\n  font-weight: 600;\n  white-space: nowrap;\n}\n\n/* ---------- HOW IT WORKS ---------- */\n.how-section {\n  padding: 88px 0;\n  background: var(--kinetic-white);\n}\n\n.how-section-header {\n  text-align: center;\n  margin-bottom: 60px;\n}\n\n.steps-row {\n  display: grid;\n  grid-template-columns: repeat(3, 1fr);\n  gap: 16px;\n  /* position: relative; */\n}\n\n.steps-row::before {\n  content: '';\n  position: absolute;\n  top: 28px;\n  left: calc(16.67% + 16px);\n  right: calc(16.67% + 16px);\n  height: 2px;\n  background: linear-gradient(90deg, var(--kinetic-teal), var(--kinetic-accent));\n  z-index: 0;\n}\n\n.step-card {\n  text-align: center;\n  padding: 32px 20px;\n  background: var(--kinetic-light);\n  border-radius: var(--radius-md);\n  border: 1px solid var(--kinetic-border);\n  position: relative;\n  z-index: 1;\n}\n\n.step-number {\n  width: 52px;\n  height: 52px;\n  background: linear-gradient(135deg, var(--kinetic-teal), var(--kinetic-accent));\n  color: white;\n  font-weight: 800;\n  font-size: 1.1rem;\n  border-radius: 50%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  margin: 0 auto 18px;\n}\n\n.step-card h3 {\n  font-size: 1rem;\n  font-weight: 700;\n  color: var(--kinetic-blue);\n  margin-bottom: 10px;\n}\n\n.step-card p {\n  font-size: 0.88rem;\n  color: var(--kinetic-gray);\n  line-height: 1.65;\n}\n\n/* ── FOOTER ─────────────────────────────────────────────────────────────────── */\n.footer {\n  background: var(--kinetic-white);\n  padding: 56px 0 0;\n}\n\n/* 4-column grid: brand | quick-links | contact | social */\n.footer-inner {\n  display: grid;\n  grid-template-columns: 1fr 1fr 1.4fr auto;\n  gap: 40px 48px;\n  align-items: flex-start;\n  padding-bottom: 48px;\n}\n\n/* ── Brand column ── */\n.footer-brand {\n  display: flex;\n  flex-direction: column;\n  gap: 14px;\n}\n\n.footer-brand-logo {\n  height: auto;\n  object-fit: contain;\n  display: block;\n  border: none;\n  outline: none;\n  box-shadow: none;\n  background: transparent;\n}\n\n.footer-tagline {\n  font-size: 0.82rem;\n  color: var(--kinetic-blue);\n  line-height: 1.65;\n  margin: 0;\n}\n\n/* ── Column headings ── */\n.footer-col-title {\n  font-size: 0.9rem;\n  font-weight: 700;\n  color: #13F01C;             \n  letter-spacing: 0.04em;\n  text-transform: uppercase;\n  margin-bottom: 18px;\n}\n\n/* ── Quick-links column ── */\n.footer-nav-list {\n  list-style: none;\n  padding: 0;\n  margin: 0;\n  display: flex;\n  flex-direction: column;\n  gap: 11px;\n}\n\n.footer-nav-list a {\n  font-size: 0.9rem;\n  color: var(--kinetic-blue);\n  text-decoration: none;\n  transition: color 0.2s;\n  line-height: 0.65;\n}\n\n.footer-nav-list a:hover { color: #4CAF50; }\n\n/* ── Contact column ── */\n.footer-contact-list {\n  list-style: none;\n  padding: 0;\n  margin: 0;\n  display: flex;\n  flex-direction: column;\n  gap: 14px;\n}\n\n.footer-contact-item {\n  font-size: 0.88rem;\n  line-height: 1.55;\n  color: var(--kinetic-blue);\n}\n\n.footer-contact-item .fci-label {\n  color: #13F01C;\n  font-weight: 600;\n  margin-right: 4px;\n}\n\n.footer-contact-item a {\n  color: var(--kinetic-blue);\n  text-decoration: none;\n  transition: color 0.2s;\n}\n\n.footer-contact-item a:hover { color: #4CAF50; }\n\n/* ── Social column ── */\n.footer-social-col {\n  display: flex;\n  flex-direction: column;\n  gap: 10px;\n}\n\n.footer-social-icons {\n  display: flex;\n  gap: 10px;\n  flex-wrap: wrap;\n}\n\n.footer-social-icon {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 38px;\n  height: 38px;\n  border-radius: 50%;\n  background: rgba(255,255,255,0.10);\n  color: var(--kinetic-blue);\n  text-decoration: none;\n  transition: background 0.2s, transform 0.2s;\n}\n\n.footer-social-icon:hover {\n  background: #4CAF50;\n  transform: translateY(-2px);\n}\n\n/* ── Responsive ── */\n@media (max-width: 960px) {\n  .footer-inner {\n    grid-template-columns: 1fr 1fr;\n    gap: 36px 32px;\n  }\n}\n\n@media (max-width: 560px) {\n  .footer-inner {\n    grid-template-columns: 1fr;\n    gap: 28px;\n  }\n  .footer-social-icons { gap: 8px; }\n}\n\n/* ---------- RESPONSIVE ---------- */\n@media (max-width: 900px) {\n  .hero-inner,\n  .web-advantage-inner,\n  .demo-grid {\n    grid-template-columns: 1fr;\n    gap: 40px;\n  }\n\n  .hero-visual { display: none; }\n\n  .steps-row {\n    grid-template-columns: 1fr;\n  }\n\n  .steps-row::before { display: none; }\n\n  .nav-links { display: none; }\n\n  .comparison-table { font-size: 0.82rem; }\n}\n\n@media (max-width: 600px) {\n  .hero { padding: 64px 0 56px; }\n  .hero h1 { font-size: 1.9rem; }\n  .benefits-grid { grid-template-columns: 1fr; }\n  .features-grid { grid-template-columns: 1fr; }\n  .differentiator-banner-inner { flex-direction: column; gap: 6px; }\n  .diff-divider { display: none; }\n}\n\n/* ── Pipe flow bubble animation (reactor & pipeline slides) ── */\n@keyframes pipe-flow {\n  0%   { transform: translateX(-12px); opacity: 0; }\n  15%  { opacity: 1; }\n  85%  { opacity: 1; }\n  100% { transform: translateX(12px);  opacity: 0; }\n}\n\n@keyframes pipe-flow-v {\n  0%   { transform: translateY(-12px); opacity: 0; }\n  15%  { opacity: 1; }\n  85%  { opacity: 1; }\n  100% { transform: translateY(12px);  opacity: 0; }\n}\n\n/* ---------- HERO KINETICS CHART — continuous animation ---------- */\n\n/* Curve draw — triggers via key change each cycle */\n.kinetic-curve {\n  stroke-dasharray: 420;\n  stroke-dashoffset: 420;\n}\n.kinetic-curve.drawn {\n  animation: curve-draw 1.1s ease forwards;\n}\n.kinetic-curve-product.drawn {\n  animation: curve-draw 1.1s ease 0.15s forwards;\n}\n\n@keyframes curve-draw {\n  to { stroke-dashoffset: 0; }\n}\n\n/* Dot pop-in (inline animation on each circle element) */\n@keyframes dot-pop {\n  0%   { opacity: 0; transform: scale(0.4); }\n  70%  { opacity: 1; transform: scale(1.15); }\n  100% { opacity: 1; transform: scale(1); }\n}\n\n/* \"Optimising…\" blink */\n@keyframes hero-blink {\n  0%, 100% { opacity: 1; }\n  50%       { opacity: 0.3; }\n}\n\n/* Floating background particles */\n.hero-particle {\n  position: absolute;\n  border-radius: 50%;\n  background: rgba(0, 194, 168, 0.18);\n  pointer-events: none;\n  animation: particle-float var(--dur, 8s) ease-in-out var(--delay, 0s) infinite alternate;\n}\n\n@keyframes particle-float {\n  0%   { transform: translateY(0px)   scale(1);    opacity: 0.15; }\n  50%  { transform: translateY(-18px) scale(1.15); opacity: 0.35; }\n  100% { transform: translateY(6px)   scale(0.9);  opacity: 0.10; }\n}\n\n@media (prefers-reduced-motion: reduce) {\n  .kinetic-curve        { stroke-dashoffset: 0; animation: none; }\n  .hero-particle        { animation: none; }\n}\n\n/* ---------- LANDING PAGE SCROLL CONTAINER ---------- */\n/* See SCROLL FIX note in the JSX: the dashboard sets html,body{overflow:hidden},\n   so the landing page owns its own scrolling region instead. */\n.kinetic-landing {\n  height: 100vh;\n  overflow-y: auto;\n}\n\n/* ---------- SCROLL ANIMATIONS ---------- */\n@media (prefers-reduced-motion: no-preference) {\n  .benefit-card,\n  .feature-card,\n  .step-card {\n    opacity: 0;\n    transform: translateY(20px);\n    transition: opacity 0.5s ease, transform 0.5s ease, box-shadow 0.25s, border-color 0.2s;\n  }\n\n  .benefit-card.visible,\n  .feature-card.visible,\n  .step-card.visible {\n    opacity: 1;\n    transform: translateY(0);\n  }\n}\n\n/* ---------- CAPABILITIES (8-card grid) + INPUT OPTIONS PANEL ---------- */\n/* 8 capability cards: four across on desktop so the grid stays 4 x 2. */\n.benefits-grid.benefits-grid-8 {\n  grid-template-columns: repeat(4, 1fr);\n  gap: 20px;\n}\n\n.benefits-grid-8 .benefit-card { padding: 26px 22px; }\n\n.benefits-grid-8 .benefit-card h3 { font-size: 1.02rem; }\n\n.inputs-panel {\n  background: var(--kinetic-white);\n  border: 1px solid var(--kinetic-border);\n  border-radius: var(--radius-md);\n  padding: 34px 32px 30px;\n  margin-bottom: 28px;\n  box-shadow: 0 2px 10px rgba(26, 60, 110, 0.05);\n}\n\n.inputs-panel-head { max-width: 680px; margin-bottom: 26px; }\n\n.inputs-panel-head h3 {\n  font-size: 1.25rem;\n  font-weight: 700;\n  color: var(--kinetic-blue);\n  margin-bottom: 8px;\n}\n\n.inputs-panel-head p {\n  font-size: 0.95rem;\n  color: var(--kinetic-gray);\n  line-height: 1.65;\n}\n\n.inputs-grid {\n  display: grid;\n  grid-template-columns: repeat(4, 1fr);\n  gap: 0;\n  border-top: 1px solid var(--kinetic-border);\n}\n\n.inputs-group {\n  padding: 22px 22px 6px 0;\n  margin-right: 22px;\n  border-right: 1px solid var(--kinetic-border);\n}\n\n.inputs-group:last-child { border-right: none; margin-right: 0; padding-right: 0; }\n\n.inputs-group h4 {\n  font-size: 0.98rem;\n  font-weight: 700;\n  color: var(--kinetic-blue);\n  margin-bottom: 6px;\n}\n\n.inputs-group p {\n  font-size: 0.86rem;\n  color: var(--kinetic-gray);\n  line-height: 1.55;\n  margin-bottom: 12px;\n}\n\n.inputs-chips {\n  list-style: none;\n  margin: 0 0 16px;\n  padding: 0;\n  display: flex;\n  flex-wrap: wrap;\n  gap: 6px;\n}\n\n.inputs-chips li {\n  background: rgba(11, 141, 166, 0.1);\n  color: var(--kinetic-teal);\n  font-size: 0.78rem;\n  font-weight: 600;\n  padding: 4px 10px;\n  border-radius: 99px;\n}\n\n@media (max-width: 1000px) {\n  .benefits-grid.benefits-grid-8 { grid-template-columns: repeat(2, 1fr); }\n  .inputs-grid { grid-template-columns: repeat(2, 1fr); }\n  .inputs-group:nth-child(2) { border-right: none; margin-right: 0; padding-right: 0; }\n  .inputs-group:nth-child(n+3) { border-top: 1px solid var(--kinetic-border); }\n  .inputs-group:nth-child(3) { padding-right: 22px; }\n}\n\n@media (max-width: 600px) {\n  .benefits-grid.benefits-grid-8 { grid-template-columns: 1fr; }\n  .inputs-panel { padding: 26px 20px 20px; }\n  .inputs-grid { grid-template-columns: 1fr; }\n  .inputs-group,\n  .inputs-group:nth-child(3) {\n    border-right: none; margin-right: 0; padding-right: 0;\n  }\n  .inputs-group:nth-child(n+2) { border-top: 1px solid var(--kinetic-border); }\n}"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
